'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useMemo, useEffect } from 'react';
import Link from 'next/link';
import clsx from 'clsx';
import { MasonryGrid } from './MasonryGrid';
import { ItemCard } from './ItemCard';
import { AddToCollectionModal } from './AddToCollectionModal';
import { useItems } from '@/lib/hooks';
import type { Item, ItemsQuery } from '@/lib/api/types';
import { useSessionState } from './SessionState';
import { FilterView } from '@/components/ui/filter-view';
import { useIsMobile } from '@/hooks/use-mobile';
import * as _ from 'es-toolkit';
import { UseInfiniteQueryOptions } from '@tanstack/react-query';
interface ItemGridViewProps {
  collectionId?: string;
  brandId?: number;
  items?: Item[];
  backLink?: {
    href: string;
    label: string;
  };
  applySessionHash?: boolean;
  nextCursor?: string;
  prevCursor?: string;
  emptyState?: {
    title: string;
    description: string;
    action?: {
      href: string;
      label: string;
    };
  };
  useQueryOptions?: Partial<UseInfiniteQueryOptions>;
  onRemoveItem?: (itemId: string) => void;
  searchEnabled?: boolean;
  initialSearchQuery?: string;
  initialSelectedTags?: string[];
  onSearchChange?: (query: string) => void;
  onTagsChange?: (tags: string[]) => void;
  variant?: 'full' | 'embedded';
  canFilter?: boolean;
  limit?: number;
}
export function ItemGridView(t0) {
  const $ = _c(74);
  const {
    collectionId,
    brandId,
    items: providedItems,
    backLink,
    applySessionHash,
    nextCursor,
    prevCursor,
    emptyState,
    onRemoveItem,
    searchEnabled,
    initialSearchQuery: t1,
    initialSelectedTags: t2,
    onSearchChange,
    onTagsChange,
    variant: t3,
    canFilter: t4,
    useQueryOptions,
    limit: t5
  } = t0;
  const initialSearchQuery = t1 === undefined ? "" : t1;
  let t6;
  if ($[0] !== t2) {
    t6 = t2 === undefined ? [] : t2;
    $[0] = t2;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const initialSelectedTags = t6;
  const variant = t3 === undefined ? "full" : t3;
  const canFilter = t4 === undefined ? false : t4;
  const limit = t5 === undefined ? 40 : t5;
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [selectedTags, setSelectedTags] = useState(initialSelectedTags);
  const [collectionModalOpen, setCollectionModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const {
    sessionHash
  } = useSessionState();
  const isMobile = useIsMobile();
  let t7;
  if ($[2] !== initialSearchQuery || $[3] !== searchQuery) {
    t7 = () => {
      if (initialSearchQuery !== searchQuery) {
        setSearchQuery(initialSearchQuery);
      }
    };
    $[2] = initialSearchQuery;
    $[3] = searchQuery;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  let t8;
  if ($[5] !== initialSearchQuery) {
    t8 = [initialSearchQuery];
    $[5] = initialSearchQuery;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  useEffect(t7, t8);
  let t9;
  if ($[7] !== initialSelectedTags) {
    t9 = initialSelectedTags.join(",");
    $[7] = initialSelectedTags;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const initialTagsKey = t9;
  let t10;
  if ($[9] !== initialSelectedTags || $[10] !== selectedTags) {
    t10 = () => {
      if (JSON.stringify(initialSelectedTags) !== JSON.stringify(selectedTags)) {
        setSelectedTags(initialSelectedTags);
      }
    };
    $[9] = initialSelectedTags;
    $[10] = selectedTags;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== initialTagsKey) {
    t11 = [initialTagsKey];
    $[12] = initialTagsKey;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  useEffect(t10, t11);
  let t12;
  bb0: {
    if (providedItems) {
      t12 = undefined;
      break bb0;
    }
    if (applySessionHash && sessionHash === undefined) {
      t12 = undefined;
      break bb0;
    }
    const t13 = searchQuery || undefined;
    let t14;
    if ($[14] !== brandId || $[15] !== limit || $[16] !== selectedTags || $[17] !== sessionHash || $[18] !== t13) {
      t14 = {
        search: t13,
        tags: selectedTags,
        brandId,
        sessionHash,
        limit
      };
      $[14] = brandId;
      $[15] = limit;
      $[16] = selectedTags;
      $[17] = sessionHash;
      $[18] = t13;
      $[19] = t14;
    } else {
      t14 = $[19];
    }
    t12 = t14;
  }
  const itemsQuery = t12;
  const t13 = !providedItems && (!applySessionHash || applySessionHash && sessionHash !== undefined);
  let t14;
  if ($[20] !== t13 || $[21] !== useQueryOptions) {
    t14 = {
      ...useQueryOptions,
      enabled: t13
    };
    $[20] = t13;
    $[21] = useQueryOptions;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const {
    data: itemsData,
    isFetching,
    error,
    fetchNextPage,
    isLoading,
    hasNextPage
  } = useItems(providedItems ? undefined : itemsQuery, t14);
  let t15;
  bb1: {
    if (providedItems) {
      t15 = providedItems;
      break bb1;
    }
    let t16;
    if ($[23] !== itemsData?.pages) {
      t16 = _.flatten(itemsData?.pages.map(_temp) || []);
      $[23] = itemsData?.pages;
      $[24] = t16;
    } else {
      t16 = $[24];
    }
    const flattenedItems = t16;
    t15 = flattenedItems;
  }
  const baseItems = t15;
  let t16;
  if ($[25] !== baseItems) {
    t16 = [...baseItems];
    $[25] = baseItems;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  const items = t16;
  const filteredItems = items;
  let t17;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = itemId => {
      setSelectedItemId(itemId);
      setCollectionModalOpen(true);
    };
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  const handleAddToCollection = t17;
  const t18 = variant === "full" && "min-h-screen bg-gradient-to-b from-gray-50 to-white";
  let t19;
  if ($[28] !== t18) {
    t19 = clsx(t18);
    $[28] = t18;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  const t20 = variant === "full" ? "max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8" : "w-full";
  let t21;
  if ($[30] !== t20) {
    t21 = clsx(t20);
    $[30] = t20;
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  const t22 = variant === "full" ? "mb-8" : "mb-6";
  let t23;
  if ($[32] !== t22) {
    t23 = clsx(t22);
    $[32] = t22;
    $[33] = t23;
  } else {
    t23 = $[33];
  }
  let t24;
  if ($[34] !== backLink) {
    t24 = backLink && <Link href={backLink.href} className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>{backLink.label}</Link>;
    $[34] = backLink;
    $[35] = t24;
  } else {
    t24 = $[35];
  }
  let t25;
  if ($[36] !== t23 || $[37] !== t24) {
    t25 = <div className={t23}>{t24}</div>;
    $[36] = t23;
    $[37] = t24;
    $[38] = t25;
  } else {
    t25 = $[38];
  }
  let t26;
  if ($[39] !== canFilter || $[40] !== onSearchChange || $[41] !== onTagsChange || $[42] !== searchEnabled || $[43] !== searchQuery || $[44] !== selectedTags) {
    t26 = canFilter && <div className="mb-8 space-y-4"><FilterView searchEnabled={searchEnabled} searchValue={searchQuery} canFilter={false} onSearch={term => {
        setSearchQuery(term);
        onSearchChange?.(term);
      }} filters={[{
        key: "tags",
        label: "Tags",
        type: "tags"
      }]} activeFilters={{
        tags: selectedTags
      }} onFilterChange={newFilters => {
        const newTags = newFilters.tags as string[] || [];
        setSelectedTags(newTags);
        onTagsChange?.(newTags);
      }} /></div>;
    $[39] = canFilter;
    $[40] = onSearchChange;
    $[41] = onTagsChange;
    $[42] = searchEnabled;
    $[43] = searchQuery;
    $[44] = selectedTags;
    $[45] = t26;
  } else {
    t26 = $[45];
  }
  let t27;
  if ($[46] !== emptyState || $[47] !== error || $[48] !== fetchNextPage || $[49] !== filteredItems || $[50] !== hasNextPage || $[51] !== isFetching || $[52] !== isLoading || $[53] !== isMobile || $[54] !== onRemoveItem || $[55] !== sessionHash) {
    t27 = isLoading ? <div className="text-center py-16"><div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center"><svg className="w-12 h-12 text-gray-400 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg></div><h3 className="text-xl font-semibold text-gray-900 mb-2">Loading products...</h3></div> : error ? <div className="text-center py-16"><div className="w-24 h-24 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center"><svg className="w-12 h-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg></div><h3 className="text-xl font-semibold text-gray-900 mb-2">Error loading products</h3><p className="text-gray-600 mb-6">Please try again later</p></div> : filteredItems.length === 0 ? <div className="text-center py-16"><div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center"><svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" /></svg></div><h3 className="text-xl font-semibold text-gray-900 mb-2">{emptyState?.title || "No products found"}</h3>{emptyState?.description != null && <p className="text-gray-600 mb-6">{emptyState?.description || "Try adjusting your search or filters"}</p>}{emptyState?.action && <Link href={emptyState.action.href} className="inline-flex items-center gap-2 px-5 py-3 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-all">{emptyState.action.label}</Link>}</div> : <><div className="block sm:hidden"><div className="grid grid-cols-2 gap-3">{filteredItems.map(item => <div key={item.id} className="relative group/card"><ItemCard item={item} onAddToCollection={handleAddToCollection} variant="compact" />{onRemoveItem && <button onClick={() => onRemoveItem(item.id)} className={clsx("absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center transition-opacity hover:bg-red-600 shadow-lg z-10 cursor-pointer", isMobile ? "opacity-100" : "opacity-0 group-hover/card:opacity-100")} aria-label="Remove from collection"><svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>}</div>)}</div></div><div className="hidden sm:block"><MasonryGrid columnWidth={280} gap={20}>{filteredItems.map(item_0 => <div key={item_0.id} className="relative group/card"><ItemCard item={item_0} onAddToCollection={handleAddToCollection} />{onRemoveItem && <button onClick={() => onRemoveItem(item_0.id)} className={clsx("absolute top-3 right-3 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center transition-opacity hover:bg-red-600 shadow-lg z-10 cursor-pointer", isMobile ? "opacity-100" : "opacity-0 group-hover/card:opacity-100")} aria-label="Remove from collection"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>}</div>)}</MasonryGrid></div>{!isFetching && hasNextPage && sessionHash && <button onClick={() => fetchNextPage()} className="w-full px-4 py-3 mt-6 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-all text-sm sm:text-base">Load more</button>}</>;
    $[46] = emptyState;
    $[47] = error;
    $[48] = fetchNextPage;
    $[49] = filteredItems;
    $[50] = hasNextPage;
    $[51] = isFetching;
    $[52] = isLoading;
    $[53] = isMobile;
    $[54] = onRemoveItem;
    $[55] = sessionHash;
    $[56] = t27;
  } else {
    t27 = $[56];
  }
  let t28;
  if ($[57] !== t21 || $[58] !== t25 || $[59] !== t26 || $[60] !== t27) {
    t28 = <div className={t21}>{t25}{t26}{t27}</div>;
    $[57] = t21;
    $[58] = t25;
    $[59] = t26;
    $[60] = t27;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = () => {
      setCollectionModalOpen(false);
      setSelectedItemId(null);
    };
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  let t30;
  if ($[63] !== filteredItems || $[64] !== selectedItemId) {
    t30 = filteredItems.find(item_1 => item_1.id === selectedItemId)?.userCollections || [];
    $[63] = filteredItems;
    $[64] = selectedItemId;
    $[65] = t30;
  } else {
    t30 = $[65];
  }
  let t31;
  if ($[66] !== collectionModalOpen || $[67] !== selectedItemId || $[68] !== t30) {
    t31 = <AddToCollectionModal isOpen={collectionModalOpen} onClose={t29} productId={selectedItemId} userCollections={t30} />;
    $[66] = collectionModalOpen;
    $[67] = selectedItemId;
    $[68] = t30;
    $[69] = t31;
  } else {
    t31 = $[69];
  }
  let t32;
  if ($[70] !== t19 || $[71] !== t28 || $[72] !== t31) {
    t32 = <div className={t19}>{t28}{t31}</div>;
    $[70] = t19;
    $[71] = t28;
    $[72] = t31;
    $[73] = t32;
  } else {
    t32 = $[73];
  }
  return t32;
}
function _temp(p) {
  return p.items;
}